import { useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/useAuth';
import { useAccountStore } from '@/stores/accountStore';
import { useFolderStore } from '@/stores/folderStore';
import { useRealtimeStore } from '@/stores/realtimeStore';
import { useTodoRealtimeStore } from '@/stores/todoRealtimeStore';
import { registerPostDrainReload, useOfflineQueueStore } from '@/stores/offlineQueueStore';
import { useEventStream } from './useEventStream';

const CONNECTING_INDICATOR_DELAY_MS = 3000;

export function RealtimeBridge() {
  const { token, isAuthenticated } = useAuth();
  const selectedAccountId = useAccountStore((state) => state.selectedAccountId);
  const accountsLoading = useAccountStore((state) => state.accountsLoading);
  const foldersLoading = useFolderStore((state) => state.foldersLoading);
  const enqueueEvent = useRealtimeStore((state) => state.enqueueEvent);
  const enqueueTodoEvent = useTodoRealtimeStore((state) => state.enqueueEvent);
  const setDocumentVisibility = useRealtimeStore((state) => state.setDocumentVisibility);
  const setTodoDocumentVisibility = useTodoRealtimeStore((state) => state.setDocumentVisibility);
  const setRealtimeStatus = useRealtimeStore((state) => state.setRealtimeStatus);
  const setShowRealtimeConnecting = useRealtimeStore(
    (state) => state.setShowRealtimeConnecting
  );
  const clearQueue = useRealtimeStore((state) => state.clearQueue);
  const clearTodoQueue = useTodoRealtimeStore((state) => state.clearQueue);
  const cancelScheduledReload = useRealtimeStore((state) => state.cancelScheduledReload);

  const wasConnectedRef = useRef(false);

  // Subscribe UNFILTERED (no account scoping): the sidebar always renders the
  // all-accounts rollup, so `mailbox.counts` for non-selected accounts must
  // arrive too (an account-filtered stream froze other accounts' badges — E4).
  // Per-event account filtering for CONTENT events stays client-side in
  // shouldProcessEvent; counts/sync events pass for every account.
  const eventAccountId = undefined;
  const realtimeEnabled = Boolean(
    isAuthenticated && token && selectedAccountId && !accountsLoading && !foldersLoading
  );

  const status = useEventStream(
    eventAccountId,
    (event) => {
      enqueueEvent(event);
      enqueueTodoEvent(event);
    },
    {
      enabled: realtimeEnabled,
      authToken: token,
      onStatusChange: setRealtimeStatus,
    }
  );

  useEffect(() => {
    if (!isAuthenticated || !token) {
      clearQueue();
      clearTodoQueue();
      cancelScheduledReload();
      setRealtimeStatus('idle');
      setShowRealtimeConnecting(false);
    }
  }, [
    cancelScheduledReload,
    clearQueue,
    clearTodoQueue,
    isAuthenticated,
    setRealtimeStatus,
    setShowRealtimeConnecting,
    token,
  ]);

  useEffect(() => {
    if (typeof document === 'undefined') {
      setDocumentVisibility(true);
      return;
    }
    const handleVisibility = () => {
      const visible = document.visibilityState !== 'hidden';
      setDocumentVisibility(visible);
      setTodoDocumentVisibility(visible);
    };
    handleVisibility();
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [setDocumentVisibility, setTodoDocumentVisibility]);

  // Reconcile missed events on SSE reconnection
  useEffect(() => {
    if (status === 'connected') {
      if (wasConnectedRef.current) {
        // This is a reconnection — refresh folder counts and reload current mailbox
        const accountId = useAccountStore.getState().selectedAccountId;
        const folderId = useFolderStore.getState().selectedFolderId();
        if (accountId) {
          useFolderStore.getState().loadFolders(accountId).catch(() => undefined);
        }
        if (accountId && folderId) {
          useRealtimeStore.getState().scheduleMailboxReload(accountId, folderId);
        }
      }
      wasConnectedRef.current = true;
      // A working SSE stream is the strongest "we're back" signal there is —
      // flush any actions queued while offline.
      void useOfflineQueueStore.getState().drain();
    }
  }, [status]);

  // Offline queue lifecycle: drain on the browser's 'online' signal and once
  // at boot (a reload while offline persists the queue in localStorage).
  useEffect(() => {
    registerPostDrainReload(() => {
      const accountId = useAccountStore.getState().selectedAccountId;
      const folderId = useFolderStore.getState().selectedFolderId();
      if (accountId) {
        useFolderStore.getState().loadFolders(accountId).catch(() => undefined);
      }
      if (accountId && folderId) {
        useRealtimeStore.getState().scheduleMailboxReload(accountId, folderId);
      }
    });
    const handleOnline = () => {
      void useOfflineQueueStore.getState().drain();
    };
    window.addEventListener('online', handleOnline);
    void useOfflineQueueStore.getState().drain();
    return () => {
      window.removeEventListener('online', handleOnline);
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      setShowRealtimeConnecting(false);
      return;
    }
    let timer: number | null = null;
    if (status === 'connecting') {
      timer = window.setTimeout(() => setShowRealtimeConnecting(true), CONNECTING_INDICATOR_DELAY_MS);
    } else {
      setShowRealtimeConnecting(false);
    }
    return () => {
      if (timer !== null) {
        window.clearTimeout(timer);
      }
    };
  }, [status, setShowRealtimeConnecting]);

  return null;
}
